import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  List,
  ListItem,
  Paper,
  Typography,
  styled,
} from "@mui/material";
import { CampaignFormSteps } from "../../../enum/campaign";
import { IWithClassName, IWithThemeProps } from "../../../theme/default.theme";
import { FileUploader } from "react-drag-drop-files";
import { CloudUpload } from "@mui/icons-material";
import { fileTypes } from "../constants";
import {
  ICampaignAdCreatives,
  ICampaignSettingsError,
  ICreative,
} from "../../../interfaces/campaign";
import Preview from "./preview";
import MediaTiles from "./media-tiles";

interface IProps extends IWithClassName {
  activeStep: CampaignFormSteps;
  form: ICampaignAdCreatives;
  error: ICampaignSettingsError;
  changeFileHandler: (file: Array<File>) => void;
}

const Tracking = ({
  activeStep,
  className,
  form,
  error,
  changeFileHandler,
}: IProps) => {
  const [preview, setPreview] = useState<File | ICreative>();
  const [addedFiles, setAddedFiles] = useState<Array<File>>([]);
  const bannerPreviewRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (form.serverBanners?.length) {
      setPreview(form.serverBanners[0]);
    } else if (addedFiles) {
      setPreview(addedFiles[0]);
    }
  }, [form, addedFiles]);

  const showPreview = (file: File | ICreative) => {
    setPreview(file);

    if (!file.hasOwnProperty("url")) {
      bannerPreviewRef.current?.setAttribute(
        "src",
        URL.createObjectURL(file as File),
      );
    } else {
      bannerPreviewRef.current?.setAttribute("src", (file as ICreative).url);
    }
  };

  const onBannerUpload = (file: Array<File>) => {
    setAddedFiles((files) => {
      const addedFiles = [...files, ...Array.from(file)];

      changeFileHandler(addedFiles);
      
      return addedFiles;
    });
  };

  const removeFile = (file: File) => {
    setAddedFiles((files) => {
      const removedFile = files.filter((f) => f !== file);

      changeFileHandler(removedFile);

      return removedFile;
    });
  };

  // useEffect(() => {
    
  // }, [addedFiles]);

  return (
    <>
      {activeStep === CampaignFormSteps.AdCreatives && (
        <Box component={Grid} className={className}>
          <Box component={Paper}>
            <FileUploader
              classes="file-upload-container"
              handleChange={onBannerUpload}
              types={fileTypes}
              multiple={true}
            >
              <List className="list-info">
                <ListItem>
                  <CloudUpload />
                </ListItem>
                <ListItem>
                  <Typography className="drop-header">
                    Select a file or drag and drop here
                  </Typography>
                </ListItem>
                <ListItem>
                  <List className="file-details">
                    <ListItem>
                      <Typography component={FormHelperText}>
                        Upload an image with size 300x50 for banner
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <Typography component={FormHelperText}>
                        Upload an image with size 320x50 for banner
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <Typography component={FormHelperText}>
                        Upload an image with size 768x1024 for interstitial
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <Typography component={FormHelperText}>
                        Upload an image with size 1200x628
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <Typography component={FormHelperText}>
                        Upload a video with aspect ratio 16:9
                      </Typography>
                    </ListItem>
                    <ListItem className="error">
                      <FormHelperText error={!!error.bannerImage}>
                        {error.bannerImage}
                      </FormHelperText>
                    </ListItem>
                  </List>
                </ListItem>
                <ListItem>
                  <Button variant="contained" color="primary">
                    Select from gallery
                  </Button>
                </ListItem>
              </List>
            </FileUploader>
          </Box>
          <Preview bannerImage={preview} bannerPreviewRef={bannerPreviewRef} />
          <MediaTiles
            files={addedFiles}
            removeFile={removeFile}
            showPreview={showPreview}
            serverFiles={form.serverBanners}
          />
        </Box>
      )}
    </>
  );
};

export default styled(Tracking)(({ theme }: IWithThemeProps) => ({
  display: "grid",
  gridTemplateColumns: "1fr 500px",
  gap: 10,
  ".file-upload-container": {
    margin: theme?.spacing(2),
    width: "inherit",
    display: "flex",
    alignItems: "center",
    border: `2px dashed ${theme?.palette.primary.main}`,
    borderRadius: 5,
    cursor: "pointer",
    justifyContent: "center",
    backgroundColor: theme?.palette.background.default,
  },
  svg: {
    width: 50,
    height: 50,
    color: theme?.palette.primary.main,
  },
  ".list-info": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
    fontWeight: "bold",
    "& li": {
      listStyle: "none",
      justifyContent: "center",
      padding: 0,
    },
    ".drop-header": {
      fontWeight: "bold",
      fontSize: 20,
    },
  },
  ".file-details li": {
    justifyContent: "left",
  },
  ".file-details li.error": {
    justifyContent: "center",
  },
}));
